/**
 * Centralized Stripe dashboard link builders.
 *
 * ALL Stripe dashboard URLs in the admin app MUST be built through this module.
 * Do not construct `dashboard.stripe.com` URLs inline or in feature-specific helpers.
 *
 * Connected-account resources use the "view-as" URL format:
 *   /{platformAccountId}/connect/view-as/{connectedAccountId}/[test/]{resource}
 * The platform account ID is fetched from the backend on admin app startup
 * (see stripe-platform-accounts.ts) and resolved from the record's currency
 * (CAD → CA platform, USD → US platform).
 *
 * When the platform account ID isn't available (fetch pending or failed, or
 * currency unknown), view-as URLs fall back to /connect/accounts/{id} (overview
 * page — functional but won't deep-link to the specific resource).
 *
 * Livemode handling:
 * - When `livemode` is explicitly `false`, URLs include the `/test/` segment.
 * - When `livemode` is `true` or unknown (`null`/`undefined`), URLs default to
 *   the live dashboard. Models that don't store `livemode` (Transaction, Payout)
 *   pass nothing and get live URLs.
 */

import { getPlatformAccountIdFromCurrency } from "./stripe-platform-accounts";

const BASE = "https://dashboard.stripe.com";

function testSegment(livemode?: boolean | null): string {
  return livemode === false ? "/test" : "";
}

/**
 * Build a "view-as" prefix for connected-account resources.
 *
 * Full format: {BASE}/{platformAccountId}/connect/view-as/{account}/[test/]
 * Fallback (no platform ID): {BASE}/connect/accounts/{account}
 */
function viewAsPrefix(
  account: string,
  opts?: { livemode?: boolean | null; currency?: string | null },
): string {
  const platformId = getPlatformAccountIdFromCurrency(opts?.currency);
  if (platformId) {
    return `${BASE}/${platformId}/connect/view-as/${account}${testSegment(opts?.livemode)}`;
  }
  return `${BASE}${testSegment(opts?.livemode)}/connect/accounts/${account}`;
}

// ── Shared option types ──────────────────────────────────────────────────────

interface ConnectedAccountOpts {
  livemode?: boolean | null;
  currency?: string | null;
  chargeType?: "direct" | "destination" | string | null;
  sourceAccount?: string | null;
}

// ── Resource links ───────────────────────────────────────────────────────────

/** Stripe event page. */
export function eventUrl(
  eventId: string,
  opts?: {
    livemode?: boolean | null;
    currency?: string | null;
    source?: "platform" | "connected";
    account?: string | null;
  },
): string {
  const { livemode, currency, source, account } = opts ?? {};
  if (source === "connected" && account) {
    return `${viewAsPrefix(account, { livemode, currency })}/events/${eventId}`;
  }
  return `${BASE}${testSegment(livemode)}/events/${eventId}`;
}

/** Stripe charge / payment page. Also works for payment intent IDs. */
export function chargeUrl(
  chargeOrPiId: string,
  opts?: ConnectedAccountOpts,
): string {
  const { livemode, currency, chargeType, sourceAccount } = opts ?? {};
  if (chargeType === "destination" && sourceAccount) {
    return `${viewAsPrefix(sourceAccount, { livemode, currency })}/payments/${chargeOrPiId}`;
  }
  return `${BASE}${testSegment(livemode)}/payments/${chargeOrPiId}`;
}

/** Stripe payment intent page. Alias for chargeUrl (Stripe uses the same path). */
export function paymentIntentUrl(
  piId: string,
  opts?: ConnectedAccountOpts,
): string {
  return chargeUrl(piId, opts);
}

/** Stripe dispute page. */
export function disputeUrl(
  disputeId: string,
  opts?: { livemode?: boolean | null },
): string {
  return `${BASE}${testSegment(opts?.livemode)}/disputes/${disputeId}`;
}

/** Stripe payout page (always under a connected account). */
export function payoutUrl(
  payoutId: string,
  sourceAccount: string,
  opts?: { livemode?: boolean | null; currency?: string | null },
): string {
  return `${viewAsPrefix(sourceAccount, opts)}/payouts/${payoutId}`;
}

/** Stripe connected account overview page. */
export function connectedAccountUrl(
  accountId: string,
  opts?: { livemode?: boolean | null },
): string {
  return `${BASE}${testSegment(opts?.livemode)}/connect/accounts/${accountId}`;
}

/** Stripe customer page. */
export function customerUrl(
  customerId: string,
  opts?: { livemode?: boolean | null },
): string {
  return `${BASE}${testSegment(opts?.livemode)}/customers/${customerId}`;
}

/** Stripe subscription page. */
export function subscriptionUrl(
  subscriptionId: string,
  opts?: { livemode?: boolean | null },
): string {
  return `${BASE}${testSegment(opts?.livemode)}/subscriptions/${subscriptionId}`;
}
